import React, { createContext, useContext, useReducer, ReactNode, useEffect, useMemo, useCallback } from 'react';
import { calculationService, CalculationInput } from '../services/calculationService';
import { CouponData } from 'modules/transactions/new-sales-entry/components/sections/CouponModal';

// Types
export interface SalesEntryItem {
  id: string;
  image?: string;
  styleNo: string;
  category: string;
  diaGoldWt: number;
  diaQty: number;
  diaClarity: string;
  diaColor: string;
  netWt: number;
  grossWt: number;
  kt: string;
  ktId: number;
  karatType: string;
  pcs: number;
  igiNo: string;
  huidNo: string;
  bagNo: string;
  mrp: number;
  tag: string;
  isSNMCC: boolean;
  isSRP: boolean;
  color: string;
  goldMrp: number;
  diaMrp: number;
  categoryName: string;
  making: number;
  makingMrp: number;
  materialTypeName: string;
  complexityName: string;
  complexityPercentage: number;
  metalAmount: number;
  colorStonePrice: number;
  subMrp: number;
  labMrp: number;
  mrpPrice: number;
  makingCharges: number;
  materialDiscount: number;
  diamondDiscount: number;
  labDiscount: number;
  mrpDiscount: number;
  todaysGoldRate: number;
  totalConsumerDiscount?: number;
  totalRewardDiscount?: number;
  materialTypeId: string;
  rewardDis?: any;
}

export interface PaymentEntryItem {
  method: string;
  amount: number;
  referenceNumber: string;
  discountAmount: number;
}

export interface OldGoldEntry {
  id: string;
  material: string;
  kt: string;
  rate: number;
  purity: number;
  netWt: number;
  equivalentWt: number;
  totalAmt: number;
  note: string;
}

export interface ConsumerOfferDetail {
  id: string;
  productId: string;
  discountOnSRPOptionId: number;
  discountTypeId: number;
  discountValue: number;
  isPercentage: boolean;
  fromDate: string;
  toDate: string;
  isStyleNo: boolean;
  styleNumber: string;
  isDefault: boolean;
  promotionName: string;
  invoiceRangeFrom: number;
  invoiceRangeTo: number;
  isActive: boolean;
  materialTypeId: number;
  materialName: string;
  discountOnSRPNmae: string; // Note: keeping original typo from API
  discountTypeNmae: string; // Note: keeping original typo from API
  discountOnMRPName: string;
  isSelected: boolean;
  styleNo?: string;
  bagNo?: string;
  originalValue?: number;
  consumerOfferOn: string;
  consumerOfferId: string;
  isClub?: boolean;
}

export interface ExchangeItem {
  id: string;
  salesOrderId: number;
  salesOrderItemId: number;
  invoiceNo: string;
  saleOrderDate: string;
  returnSalesOrderId: number;
  returnSaleOrderDate: string;
  categoryName: string;
  diaWt: number;
  netWt: number;
  diaQty: number;
  quantity: number;
  mrpPrice: number;
  finalSaleOrderPrice: number;
  materialTypeId: number;
  materialTypeName: string;
  metalAmount: number;
  laborAmount: number;
  otherCharges: number;
  discountAmount: number;
  totalSoAmount: number;
  finalSoAmount: number;
  returnAmount: number;
  finalDueAmount: number;
  materialDiscount: number;
  diamondDiscount: number;
  labDiscount: number;
  mrpDiscount: number;
  customerId: number;
  storeId: number;
  salesPersonId: number;
  cgst: number;
  sgst: number;
  subAmount: number;
  isOnGst: boolean;
  vocAmount: number;
  totalReturnSaleOrderAmount: number;
}

export interface ExchangeInvoice {
  invoiceNo: string;
  customerName: string;
  items: ExchangeItem[];
  totalAmount: number;
  totalWeight: number;
  totalItems: number;
}

export interface ExchangeData {
  invoices: ExchangeInvoice[];
  totalAmount: number;
  totalWeight: number;
  totalItems: number;
}

export interface SalesEntryData {
  exchangeSalesReturnData: any;
  bagNo: string;
  styleNo: string;
  igiNo: string;
  huid: string;
  customer: {
    id: string;
    name: string;
    phone: string;
    email: string;
    isIgst?: boolean;
    creditAmount?: number;
    rewardPoints?: number;
    BillingAddressId?: string | null | number;
    ShippingAddressId?: string | null | number;
  } | null;
  salesPerson: {
    id: string;
    name: string;
  } | null;
  coupon: CouponData | null;
  discount: number;
  discountType?: 'percentage' | 'amount' | null;
  payment: PaymentEntryItem[] | null;
  oldGold: {
    amount: number;
    weight: number;
  } | null;
  oldGoldEntries: OldGoldEntry[];
  exchange: ExchangeData | null;
  redemption: {
    points: number;
    amount: number;
  } | null;
  rewardPoints: number;
  items: SalesEntryItem[];
  consumerOfferDetail?: ConsumerOfferDetail[];
  summary: {
    ornamentAmt: number;
    makingCharges: number;
    subAmt: number;
    consumerOffer: number;
    storeOffer: number;
    discountVoucher: number;
    totalOffer: number;
    taxableAmount: number;
    cgst: number;
    sgst: number;
    totalTax: number;
    totalAmt: number;
    totalDueAmt: number;
  };
  voucherDetails: {
    oldGoldAmt: number;
    exchangeAmt: number;
    voucherAmt: number;
  };
  customerDetails: {
    creditAmt: number;
    rewardPoints: number;
  };
  materialTypes?: MaterialType[];

  rewardRedeemDetailsModelsList: {
    id: number;
    materialTypeId: number;
    usedPoints: number;
    amountConverted: number;
    totalAmt: number;
    rewardPercentage: number;
  }[];

  draftId?: string;
  isDraft?: boolean;

  exchItem?: any;
  exchCalData?: any;
}

export interface MaterialType {
  materialTypeId: number;
  materialTypeName: string;
  perRewardPointRs: number;
  isClub: boolean;
  maxPercentageOnInvoice: number;
  validFrom: string;
  isActive: boolean;
  id: number;
}

type Distribution = {
  [matId: number]: {
    materialTypeName: string;
    items: { [productId: number]: number };
  };
};


type SalesEntryAction =
  | { type: 'SET_BAG_NO'; payload: string }
  | { type: 'SET_EXCHANGE_SALES_RETURN'; payload: any }
  | { type: 'REMOVE_EXCHANGE_SALES_RETURN'; payload: { id: string } }
  | { type: 'SET_STYLE_NO'; payload: string }
  | { type: 'SET_IGI_NO'; payload: string }
  | { type: 'SET_HUID'; payload: string }
  | { type: 'SET_CUSTOMER'; payload: SalesEntryData['customer'] }
  | { type: 'SET_SALES_PERSON'; payload: SalesEntryData['salesPerson'] }
  | { type: 'SET_COUPON'; payload: SalesEntryData['coupon'] }
  | { type: 'SET_DISCOUNT'; payload: { value: number; type?: 'percentage' | 'amount' | null } }
  | { type: 'ADD_PAYMENT'; payload: PaymentEntryItem }
  | { type: 'REMOVE_PAYMENT'; payload: number } // Remove by index
  | { type: 'UPDATE_PAYMENT'; payload: { index: number; data: Partial<PaymentEntryItem> } }
  | { type: 'CLEAR_PAYMENTS' }
  | { type: 'SET_OLD_GOLD'; payload: SalesEntryData['oldGold'] }
  | { type: 'SET_OLD_GOLD_ENTRIES'; payload: OldGoldEntry[] }
  | { type: 'SET_EXCHANGE'; payload: SalesEntryData['exchange'] }
  | { type: 'SET_EXCHANGE_DATA'; payload: ExchangeData }
  | { type: 'ADD_EXCHANGE_INVOICE'; payload: ExchangeInvoice }
  | { type: 'REMOVE_EXCHANGE_INVOICE'; payload: string } // Remove by invoiceNo
  | { type: 'CLEAR_EXCHANGE_DATA' }
  | { type: 'SET_REDEMPTION'; payload: SalesEntryData['redemption'] }
  | { type: 'SET_REWARD_POINTS'; payload: number }
  | { type: 'ADD_ITEM'; payload: SalesEntryItem }
  | { type: 'UPDATE_ITEM'; payload: { id: string; data: Partial<SalesEntryItem> } }
  | { type: 'REMOVE_ITEM'; payload: { id: string, index: number } }
  | { type: 'ADD_CONSUMER_OFFER_DETAIL'; payload: ConsumerOfferDetail }
  | { type: 'ADD_MULTIPLE_CONSUMER_OFFER_DETAILS'; payload: ConsumerOfferDetail[] }
  | { type: 'UPDATE_CONSUMER_OFFER_DETAIL'; payload: { id: string; data: Partial<ConsumerOfferDetail> } }
  | { type: 'REMOVE_CONSUMER_OFFER_DETAIL'; payload: string }
  | { type: 'TOGGLE_CONSUMER_OFFER_SELECTION'; payload: string }
  | { type: 'CLEAR_CONSUMER_OFFER_DETAILS' }
  | { type: 'REMOVE_CONSUMER_OFFER_DETAILS_BY_PRODUCT'; payload: string }
  | { type: 'UPDATE_SUMMARY'; payload: Partial<SalesEntryData['summary']> }
  | { type: 'UPDATE_VOUCHER_DETAILS'; payload: Partial<SalesEntryData['voucherDetails']> }
  | { type: 'UPDATE_CUSTOMER_DETAILS'; payload: Partial<SalesEntryData['customerDetails']> }
  | { type: 'RESET_FORM' }
  | { type: 'SET_MATERIAL_TYPES'; payload: MaterialType[] }
  | { type: 'ADD_MATERIAL_TYPE'; payload: MaterialType }
  | { type: 'REMOVE_MATERIAL_TYPE'; payload: number }
  | { type: "APPLY_REWARD_POINTS"; payload: { distribution: { [id: number]: number } } }
  | { type: 'REMOVE_REWARD_POINTS'; }
  | { type: 'SET_REWARD_REDEEM_DETAILS'; payload: SalesEntryData['rewardRedeemDetailsModelsList'] }
  | { type: 'ADD_REWARD_REDEEM_DETAIL'; payload: SalesEntryData['rewardRedeemDetailsModelsList'][0] }
  | { type: 'REMOVE_REWARD_REDEEM_DETAIL'; payload: number } // remove by id
  | { type: 'CLEAR_REWARD_REDEEM_DETAILS' }
  | { type: "SET_DRAFT_ID"; payload: string }
  | { type: "SET_DATA"; payload: Partial<SalesEntryData> }
  | { type: "SET_EXT_ITEM"; payload: any }
  | { type: "SET_EXT_CAL_DATA"; payload: any };


const initialState: SalesEntryData = {
  exchangeSalesReturnData: '',
  bagNo: '',
  styleNo: '',
  igiNo: '',
  huid: '',
  customer: null,
  salesPerson: null,
  coupon: null,
  discount: 0,
  discountType: null,
  payment: [],
  oldGold: null,
  oldGoldEntries: [],
  exchange: null,
  redemption: null,
  rewardPoints: 0,
  items: [],
  consumerOfferDetail: [],
  summary: {
    ornamentAmt: 0,
    makingCharges: 0,
    subAmt: 0,
    consumerOffer: 0,
    storeOffer: 0,
    discountVoucher: 0,
    totalOffer: 0,
    taxableAmount: 0,
    cgst: 0,
    sgst: 0,
    totalTax: 0,
    totalAmt: 0,
    totalDueAmt: 0,
  },
  voucherDetails: {
    oldGoldAmt: 0,
    exchangeAmt: 0,
    voucherAmt: 0,
  },
  customerDetails: {
    creditAmt: 0,
    rewardPoints: 0,
  },
  materialTypes: [],
  rewardRedeemDetailsModelsList: [],
  draftId: "",
  isDraft: false,
  exchItem: null,
  exchCalData: null,
};

function salesEntryReducer(state: SalesEntryData, action: SalesEntryAction): SalesEntryData {
  switch (action.type) {
    case "SET_DRAFT_ID":
      return {
        ...state,
        draftId: action.payload,
      };
    case "SET_DATA":
      return {
        ...state,
        ...action.payload,
        isDraft: true,
      };
    case 'SET_EXCHANGE_SALES_RETURN':
      return {
        ...state,
        exchangeSalesReturnData: action.payload,
      };
    case 'SET_BAG_NO':
      return { ...state, bagNo: action.payload };
    case 'SET_STYLE_NO':
      return { ...state, styleNo: action.payload };
    case 'SET_IGI_NO':
      return { ...state, igiNo: action.payload };
    case 'SET_HUID':
      return { ...state, huid: action.payload };
    case 'SET_CUSTOMER':
      return { ...state, customer: action.payload };
    case 'SET_SALES_PERSON':
      return { ...state, salesPerson: action.payload };
    case 'SET_COUPON':
      return { ...state, coupon: action.payload };
    case 'SET_DISCOUNT':
      return {
        ...state,
        discount: action.payload.value,
        discountType: action.payload.type ?? null,
      };
    case 'ADD_PAYMENT':
      return { ...state, payment: [...(state?.payment || []), action.payload] };
    case 'REMOVE_PAYMENT':
      return {
        ...state,
        payment: (state?.payment || []).filter((_, index) => index !== action.payload)
      };
    case 'UPDATE_PAYMENT':
      return {
        ...state,
        payment: (state?.payment || []).map((payment, index) =>
          index === action.payload.index
            ? { ...payment, ...action.payload.data }
            : payment
        )
      };
    case 'CLEAR_PAYMENTS':
      return { ...state, payment: [] };
    case 'SET_OLD_GOLD':
      return { ...state, oldGold: action.payload };
    case 'SET_OLD_GOLD_ENTRIES':
      return { ...state, oldGoldEntries: action.payload };
    case 'SET_EXCHANGE':
      return { ...state, exchange: action.payload };
    case 'SET_EXCHANGE_DATA':
      return { ...state, exchange: action.payload };
    case 'ADD_EXCHANGE_INVOICE': {
      const existingExchange = state?.exchange;
      const newInvoice = action.payload;

      if (!existingExchange) {
        // First exchange invoice
        return {
          ...state,
          exchange: {
            invoices: [newInvoice],
            totalAmount: newInvoice.totalAmount,
            totalWeight: newInvoice.totalWeight,
            totalItems: newInvoice.totalItems,
          }
        };
      }

      // Check if invoice already exists and replace it, otherwise add it
      const existingInvoiceIndex = existingExchange.invoices.findIndex(
        inv => inv.invoiceNo === newInvoice.invoiceNo
      );

      let updatedInvoices;
      if (existingInvoiceIndex >= 0) {
        // Replace existing invoice
        updatedInvoices = existingExchange.invoices.map((inv, index) =>
          index === existingInvoiceIndex ? newInvoice : inv
        );
      } else {
        // Add new invoice
        updatedInvoices = [...existingExchange.invoices, newInvoice];
      }

      // Recalculate totals
      const totalAmount = updatedInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
      const totalWeight = updatedInvoices.reduce((sum, inv) => sum + inv.totalWeight, 0);
      const totalItems = updatedInvoices.reduce((sum, inv) => sum + inv.totalItems, 0);

      return {
        ...state,
        exchange: {
          invoices: updatedInvoices,
          totalAmount,
          totalWeight,
          totalItems,
        }
      };
    }
    case 'REMOVE_EXCHANGE_INVOICE': {
      const existingExchange = state?.exchange;
      if (!existingExchange) return state;

      const updatedInvoices = existingExchange.invoices.filter(
        inv => inv.invoiceNo !== action.payload
      );

      if (updatedInvoices.length === 0) {
        return { ...state, exchange: null };
      }

      // Recalculate totals
      const totalAmount = updatedInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
      const totalWeight = updatedInvoices.reduce((sum, inv) => sum + inv.totalWeight, 0);
      const totalItems = updatedInvoices.reduce((sum, inv) => sum + inv.totalItems, 0);

      return {
        ...state,
        exchange: {
          invoices: updatedInvoices,
          totalAmount,
          totalWeight,
          totalItems,
        }
      };
    }
    case 'CLEAR_EXCHANGE_DATA':
      return { ...state, exchange: null };
    case 'SET_REDEMPTION':
      return { ...state, redemption: action.payload };
    case 'SET_REWARD_POINTS':
      return { ...state, rewardPoints: action.payload };
    case 'ADD_ITEM':
      return { ...state, items: [...state?.items, action.payload] };
    case 'UPDATE_ITEM':
      return {
        ...state,
        items: state?.items.map(item =>
          item.id === action.payload.id
            ? { ...item, ...action.payload.data }
            : item
        ),
      };
    case 'REMOVE_ITEM': {
      // When removing an item, also remove associated consumer offers
      const filteredItems = state?.items.filter((item, index) => index !== action.payload.index);
      const filteredOffers = (state?.consumerOfferDetail || []).filter(offer => offer.productId !== action.payload.id);
      return {
        ...state,
        items: filteredItems,
        consumerOfferDetail: filteredOffers,
      };
    }
    case 'UPDATE_SUMMARY':
      return {
        ...state,
        summary: { ...state?.summary, ...action.payload },
      };
    case 'UPDATE_VOUCHER_DETAILS':
      return {
        ...state,
        voucherDetails: { ...state?.voucherDetails, ...action.payload },
      };
    case 'UPDATE_CUSTOMER_DETAILS':
      return {
        ...state,
        customerDetails: { ...state?.customerDetails, ...action.payload },
      };
    case 'ADD_CONSUMER_OFFER_DETAIL':
      return {
        ...state,
        consumerOfferDetail: [...(state?.consumerOfferDetail || []), action.payload],
      };
    case 'ADD_MULTIPLE_CONSUMER_OFFER_DETAILS':
      return {
        ...state,
        consumerOfferDetail: [...(state?.consumerOfferDetail || []), ...action.payload],
      };
    case 'UPDATE_CONSUMER_OFFER_DETAIL':
      return {
        ...state,
        consumerOfferDetail: (state?.consumerOfferDetail || []).map(offer =>
          offer.id === action.payload.id
            ? { ...offer, ...action.payload.data }
            : offer
        ),
      };
    case 'REMOVE_CONSUMER_OFFER_DETAIL':
      return {
        ...state,
        consumerOfferDetail: (state?.consumerOfferDetail || []).filter(offer => offer.id !== action.payload),
      };
    case 'TOGGLE_CONSUMER_OFFER_SELECTION': {
      const data = {
        ...state,
        consumerOfferDetail: (state?.consumerOfferDetail || []).map(offer =>
          offer.id === action.payload
            ? { ...offer, isSelected: !offer.isSelected }
            : offer
        ),
      };
      data.items = data.items.map(product => {
        let mrpDiscount = 0;
        let labDiscount = 0;
        let materialDiscount = 0;
        let diamondDiscount = 0;

        (data.consumerOfferDetail || [])
          .filter(offer => offer.isSelected && (offer.productId === product.id || !offer.productId))
          .forEach(offer => {
            const discountValue = offer.discountValue;

            if (offer.discountTypeNmae === "Flat") {
              if (offer.discountOnMRPName === "Total Price") {
                const calc = offer.isPercentage
                  ? (product.mrpPrice * discountValue / 100)
                  : discountValue;
                mrpDiscount += calc;
                offer.originalValue = calc;
              }

              if (offer.discountOnSRPNmae) {
                switch (offer.discountOnSRPNmae) {
                  case 'Labour': {
                    const calc = offer.isPercentage
                      ? (product.makingCharges * discountValue / 100)
                      : discountValue;
                    labDiscount += calc;
                    offer.originalValue = calc;
                    break;
                  }
                  case 'Gold': {
                    const calc = offer.isPercentage
                      ? (product.metalAmount * discountValue / 100)
                      : discountValue;
                    materialDiscount += calc;
                    offer.originalValue = calc;
                    break;
                  }
                  case 'Diamond': {
                    const calc = offer.isPercentage
                      ? (product.diaMrp * discountValue / 100)
                      : discountValue;
                    diamondDiscount += calc;
                    offer.originalValue = calc;
                    break;
                  }
                }
              }
            }
          });

        return {
          ...product,
          mrpDiscount,
          labDiscount,
          materialDiscount,
          diamondDiscount,
          totalConsumerDiscount: mrpDiscount + labDiscount + materialDiscount + diamondDiscount,
        };
      });

      const invoiceRangeOffers = data.consumerOfferDetail.filter(
        offer => offer.isSelected && offer.discountTypeNmae === "Invoice Range"
      );

      if (invoiceRangeOffers.length > 0) {
        let itemsForDiscount = data.items.map(item => ({
          ...item,
          invoiceRangeDiscountValue: 0,
          mrpDiscount: item.mrpDiscount || 0,
          labDiscount: item.labDiscount || 0,
          materialDiscount: item.materialDiscount || 0,
          diamondDiscount: item.diamondDiscount || 0,
          totalConsumerDiscount:
            (item.mrpDiscount || 0) +
            (item.labDiscount || 0) +
            (item.materialDiscount || 0) +
            (item.diamondDiscount || 0),
        }));

        const matchedItems = itemsForDiscount.filter(item =>
          invoiceRangeOffers?.some(
            offer =>
              offer.materialName === item.materialTypeName &&
              ((offer.discountOnSRPOptionId ? true : false) == item.isSRP)
          )
        );

        const matchedOffers = invoiceRangeOffers.filter(offer =>
          data.items.some(item => offer.materialName === item.materialTypeName)
        );

        const newMatchedItems = matchedItems.filter(item =>
          invoiceRangeOffers?.some(
            offer => offer.isStyleNo && item.styleNo == offer.styleNumber
          )
        );

        const newMatchedOffers = invoiceRangeOffers.filter(offer =>
          data.items.some(
            item =>
              offer.materialName === item.materialTypeName &&
              (offer.isStyleNo && offer.styleNumber == item.styleNo)
          )
        );

        const applyInvoiceRangeDiscounts = (items: any[], offers: any[]) => {
          if (!items.length || !offers.length) return items;

          const totalNetPrice = items.reduce(
            (sum, i) => sum + (i.mrpPrice - (i.totalConsumerDiscount - (i.invoiceRangeDiscountValue || 0))),
            0
          );

          return items.map(item => {
            let { mrpDiscount, labDiscount, materialDiscount, diamondDiscount } = item;

            offers.forEach(offer => {
              const netPrice = item.mrpPrice - (item.totalConsumerDiscount - (item.invoiceRangeDiscountValue || 0));
              const proportion = totalNetPrice > 0 ? netPrice / totalNetPrice : 0;

              const proportionalDiscount = offer.isPercentage
                ? (netPrice * offer.discountValue) / 100 // percentage
                : proportion * offer.discountValue;     // fixed ₹

              if (offer.discountOnMRPName === "Total Price") mrpDiscount += proportionalDiscount;
              if (offer.discountOnSRPNmae) {
                switch (offer.discountOnSRPNmae) {
                  case "Labour": labDiscount += proportionalDiscount; break;
                  case "Gold": materialDiscount += proportionalDiscount; break;
                  case "Diamond": diamondDiscount += proportionalDiscount; break;
                }
              }

              offer.originalValueTemp = (offer.originalValueTemp ?? 0) + proportionalDiscount;
            });

            const total = mrpDiscount + labDiscount + materialDiscount + diamondDiscount;

            return {
              ...item,
              invoiceRangeDiscountValue: total - item.totalConsumerDiscount,
              mrpDiscount,
              labDiscount,
              materialDiscount,
              diamondDiscount,
              totalConsumerDiscount: total,
            };
          });
        };

        const updatedMatchedItems = applyInvoiceRangeDiscounts(matchedItems, matchedOffers);

        const updatedNewMatchedItems = applyInvoiceRangeDiscounts(newMatchedItems, newMatchedOffers);

        data.items = data.items.map(item => {
          return (
            updatedMatchedItems.find(u => u.id === item.id) ||
            updatedNewMatchedItems.find(u => u.id === item.id) ||
            item
          );
        });
      }

      return data;
    }
    case 'SET_MATERIAL_TYPES':
      return {
        ...state,
        materialTypes: action.payload
      };
    case 'ADD_MATERIAL_TYPE':
      return { ...state, materialTypes: [...(state.materialTypes || []), action.payload] };

    case 'REMOVE_MATERIAL_TYPE':
      return {
        ...state,
        materialTypes: (state.materialTypes || []).filter(mt => mt.id !== action.payload),
      };
    case 'SET_REWARD_REDEEM_DETAILS':
      return { ...state, rewardRedeemDetailsModelsList: action.payload };

    case 'ADD_REWARD_REDEEM_DETAIL':
      return {
        ...state,
        rewardRedeemDetailsModelsList: [
          ...state.rewardRedeemDetailsModelsList,
          action.payload,
        ],
      };

    case 'REMOVE_REWARD_REDEEM_DETAIL':
      return {
        ...state,
        rewardRedeemDetailsModelsList: state.rewardRedeemDetailsModelsList.filter(
          detail => detail.id !== action.payload
        ),
      };

    case 'CLEAR_REWARD_REDEEM_DETAILS':
      return { ...state, rewardRedeemDetailsModelsList: [] };

    case "APPLY_REWARD_POINTS": {
      const distribution = action.payload?.distribution as any;

      const data = {
        ...state,
        items: state.items.map((product: any) => {
          const materialTypeId = product.materialTypeId; // e.g., "1"
          const productId = product.id; // e.g., "16"

          let rewardValue = 0;

          if (
            distribution[materialTypeId] &&
            distribution[materialTypeId].items &&
            distribution[materialTypeId].items[productId]
          ) {
            rewardValue = distribution[materialTypeId].items[productId];
          }

          return {
            ...product,
            rewardDis: rewardValue,
            totalRewardDiscount:
              (product.rewardDis || 0) +
              rewardValue,
          };
        }),
      };

      return data;
    }


    case "REMOVE_REWARD_POINTS": {
      const data = {
        ...state,
        items: state.items.map(product => {
          const rewardValue = product.rewardDis || 0;
          return {
            ...product,
            rewardDis: 0,
            totalRewardDiscount: 0,
          };
        }),
      };

      return data;
    }


    case 'CLEAR_CONSUMER_OFFER_DETAILS':
      return {
        ...state,
        consumerOfferDetail: [],
      };
    case 'REMOVE_EXCHANGE_SALES_RETURN': {
      const currentItems = state.exchangeSalesReturnData?.returnSalesOrderItems || [];

      const filteredItems = currentItems.filter(
        (item: any) => item.id.toString() !== action.payload.id.toString()
      );

      const totalExchangeAmt = filteredItems.reduce(
        (sum: number, item: any) => sum + (item.returnSaleOrderAmount || 0),
        0
      );

      return {
        ...state,
        exchangeSalesReturnData: {
          ...state.exchangeSalesReturnData,
          returnSalesOrderItems: filteredItems,
          returnSalesOrderModel: {
            ...state.exchangeSalesReturnData.returnSalesOrderModel,
            totalReturnSaleOrderAmount: totalExchangeAmt,
          },
        },
        voucherDetails: {
          ...state.voucherDetails,
          exchangeAmt: totalExchangeAmt,
          voucherAmt: totalExchangeAmt,
        },
        summary: {
          ...state.summary,
          totalDueAmt: (state.summary?.totalAmt || 0) - totalExchangeAmt,
        },
      };
    }
    case 'REMOVE_CONSUMER_OFFER_DETAILS_BY_PRODUCT':
      return {
        ...state,
        consumerOfferDetail: (state?.consumerOfferDetail || []).filter(offer => offer.productId !== action.payload),
      };
    case "SET_EXT_ITEM":
      return {
        ...state,
        exchItem: action.payload,
      };
    case "SET_EXT_CAL_DATA":
      return {
        ...state,
        exchCalData: action.payload,
      };
    case 'RESET_FORM':
      return initialState;
    default:
      return state;
  }

}

interface SalesEntryContextType {
  cid?: string;
  coid?: string;
  state: SalesEntryData;
  dispatch: React.Dispatch<SalesEntryAction>;
  actions: {
    setDraftId: (id: string) => void;
    setData: (data: Partial<SalesEntryData>) => void;
    exchagneSellesReturn(data: any): unknown;
    removeExchangeSalesReturn: (id: string) => void;
    setBagNo: (bagNo: string) => void;
    setStyleNo: (styleNo: string) => void;
    setIgiNo: (igiNo: string) => void;
    setHuid: (huid: string) => void;
    setCustomer: (customer: SalesEntryData['customer']) => void;
    setSalesPerson: (salesPerson: SalesEntryData['salesPerson']) => void;
    setCoupon: (coupon: SalesEntryData['coupon']) => void;
    setDiscount: (discount: number, type?: 'percentage' | 'amount') => void;
    addPayment: (payment: PaymentEntryItem) => void;
    removePayment: (index: number) => void;
    updatePayment: (index: number, data: Partial<PaymentEntryItem>) => void;
    clearPayments: () => void;
    setOldGold: (oldGold: SalesEntryData['oldGold']) => void;
    setOldGoldEntries: (entries: OldGoldEntry[]) => void;
    setExchange: (exchange: SalesEntryData['exchange']) => void;
    setExchangeData: (exchangeData: ExchangeData) => void;
    addExchangeInvoice: (invoice: ExchangeInvoice) => void;
    removeExchangeInvoice: (invoiceNo: string) => void;
    clearExchangeData: () => void;
    setRedemption: (redemption: SalesEntryData['redemption']) => void;
    setRewardPoints: (points: number) => void;
    addItem: (item: SalesEntryItem) => void;
    updateItem: (id: string, data: Partial<SalesEntryItem>) => void;
    removeItem: (id: string, index: number) => void;
    addConsumerOfferDetail: (offer: ConsumerOfferDetail) => void;
    addMultipleConsumerOfferDetails: (offers: ConsumerOfferDetail[]) => void;
    updateConsumerOfferDetail: (id: string, data: Partial<ConsumerOfferDetail>) => void;
    removeConsumerOfferDetail: (id: string) => void;
    toggleConsumerOfferSelection: (id: string) => void;
    clearConsumerOfferDetails: () => void;
    removeConsumerOfferDetailsByProduct: (productId: string) => void;
    updateSummary: (summary: Partial<SalesEntryData['summary']>) => void;
    updateVoucherDetails: (details: Partial<SalesEntryData['voucherDetails']>) => void;
    updateCustomerDetails: (details: Partial<SalesEntryData['customerDetails']>) => void;
    resetForm: () => void;
    setMaterialTypes(data: any): unknown;
    applyRewardPoints: (distribution: Distribution) => void;
    removeRewardPoints: () => void;
    setRewardRedeemDetails: (list: SalesEntryData['rewardRedeemDetailsModelsList']) => void;
    addRewardRedeemDetail: (detail: SalesEntryData['rewardRedeemDetailsModelsList'][0]) => void;
    removeRewardRedeemDetail: (id: number) => void;
    clearRewardRedeemDetails: () => void;
    setExchItem: (items: any) => void;
    setExchCalData: (data: any) => void;
  };
}


const SalesEntryContext = createContext<SalesEntryContextType | undefined>(undefined);
interface ProviderProps {
  coid?: string;
  cid?: string;
  children: React.ReactNode;
}
export const SalesEntryProvider: React.FC<ProviderProps> = ({ coid, cid, children }) => {
  const [state, dispatch] = useReducer(salesEntryReducer, initialState);

  const actions = useMemo(() => ({

    setData: (data: Partial<SalesEntryData>) =>
      dispatch({ type: "SET_DATA", payload: data }),
    setDraftId: (id: string) =>
      dispatch({ type: "SET_DRAFT_ID", payload: id }),

    exchagneSellesReturn: (data: any) => dispatch({ type: 'SET_EXCHANGE_SALES_RETURN', payload: data }),
    removeExchangeSalesReturn: (id: string) => {
      return dispatch({
        type: 'REMOVE_EXCHANGE_SALES_RETURN',
        payload: { id }
      });
    },
    setBagNo: (bagNo: string) => dispatch({ type: 'SET_BAG_NO', payload: bagNo }),
    setStyleNo: (styleNo: string) => dispatch({ type: 'SET_STYLE_NO', payload: styleNo }),
    setIgiNo: (igiNo: string) => dispatch({ type: 'SET_IGI_NO', payload: igiNo }),
    setHuid: (huid: string) => dispatch({ type: 'SET_HUID', payload: huid }),
    setCustomer: (customer: SalesEntryData['customer']) => dispatch({ type: 'SET_CUSTOMER', payload: customer }),
    setSalesPerson: (salesPerson: SalesEntryData['salesPerson']) => dispatch({ type: 'SET_SALES_PERSON', payload: salesPerson }),
    setCoupon: (coupon: SalesEntryData['coupon']) => dispatch({ type: 'SET_COUPON', payload: coupon }),
    setDiscount: (discount: number, type?: 'percentage' | 'amount' | null) => dispatch({ type: 'SET_DISCOUNT', payload: { value: discount, type } }),
    addPayment: (payment: PaymentEntryItem) => dispatch({ type: 'ADD_PAYMENT', payload: payment }),
    removePayment: (index: number) => dispatch({ type: 'REMOVE_PAYMENT', payload: index }),
    updatePayment: (index: number, data: Partial<PaymentEntryItem>) => dispatch({ type: 'UPDATE_PAYMENT', payload: { index, data } }),
    clearPayments: () => dispatch({ type: 'CLEAR_PAYMENTS' }),
    setOldGold: (oldGold: SalesEntryData['oldGold']) => dispatch({ type: 'SET_OLD_GOLD', payload: oldGold }),
    setOldGoldEntries: (entries: OldGoldEntry[]) => dispatch({ type: 'SET_OLD_GOLD_ENTRIES', payload: entries }),
    setExchange: (exchange: SalesEntryData['exchange']) => dispatch({ type: 'SET_EXCHANGE', payload: exchange }),
    setExchangeData: (exchangeData: ExchangeData) => dispatch({ type: 'SET_EXCHANGE_DATA', payload: exchangeData }),
    addExchangeInvoice: (invoice: ExchangeInvoice) => dispatch({ type: 'ADD_EXCHANGE_INVOICE', payload: invoice }),
    removeExchangeInvoice: (invoiceNo: string) => dispatch({ type: 'REMOVE_EXCHANGE_INVOICE', payload: invoiceNo }),
    clearExchangeData: () => dispatch({ type: 'CLEAR_EXCHANGE_DATA' }),
    setRedemption: (redemption: SalesEntryData['redemption']) => dispatch({ type: 'SET_REDEMPTION', payload: redemption }),
    setRewardPoints: (points: number) => dispatch({ type: 'SET_REWARD_POINTS', payload: points }),
    addItem: (item: SalesEntryItem) => dispatch({ type: 'ADD_ITEM', payload: item }),
    updateItem: (id: string, data: Partial<SalesEntryItem>) => dispatch({ type: 'UPDATE_ITEM', payload: { id, data } }),
    removeItem: (id: string, index: number) => dispatch({ type: 'REMOVE_ITEM', payload: { id, index } }),
    addConsumerOfferDetail: (offer: ConsumerOfferDetail) => dispatch({ type: 'ADD_CONSUMER_OFFER_DETAIL', payload: offer }),
    addMultipleConsumerOfferDetails: (offers: ConsumerOfferDetail[]) => dispatch({ type: 'ADD_MULTIPLE_CONSUMER_OFFER_DETAILS', payload: offers }),
    updateConsumerOfferDetail: (id: string, data: Partial<ConsumerOfferDetail>) => dispatch({ type: 'UPDATE_CONSUMER_OFFER_DETAIL', payload: { id, data } }),
    removeConsumerOfferDetail: (id: string) => dispatch({ type: 'REMOVE_CONSUMER_OFFER_DETAIL', payload: id }),
    toggleConsumerOfferSelection: (id: string) => dispatch({ type: 'TOGGLE_CONSUMER_OFFER_SELECTION', payload: id }),
    clearConsumerOfferDetails: () => dispatch({ type: 'CLEAR_CONSUMER_OFFER_DETAILS' }),
    removeConsumerOfferDetailsByProduct: (productId: string) => dispatch({ type: 'REMOVE_CONSUMER_OFFER_DETAILS_BY_PRODUCT', payload: productId }),
    updateSummary: (summary: Partial<SalesEntryData['summary']>) => dispatch({ type: 'UPDATE_SUMMARY', payload: summary }),
    updateVoucherDetails: (details: Partial<SalesEntryData['voucherDetails']>) => dispatch({ type: 'UPDATE_VOUCHER_DETAILS', payload: details }),
    updateCustomerDetails: (details: Partial<SalesEntryData['customerDetails']>) => dispatch({ type: 'UPDATE_CUSTOMER_DETAILS', payload: details }),
    resetForm: () => dispatch({ type: 'RESET_FORM' }),
    setMaterialTypes: (data: any) => dispatch({ type: 'SET_MATERIAL_TYPES', payload: data }),
    applyRewardPoints: (distribution: any) => dispatch({ type: "APPLY_REWARD_POINTS", payload: { distribution } }),
    removeRewardPoints: () => dispatch({ type: "REMOVE_REWARD_POINTS" }),
    setRewardRedeemDetails: (list: any) => dispatch({ type: 'SET_REWARD_REDEEM_DETAILS', payload: list }),
    addRewardRedeemDetail: (detail: any) => dispatch({ type: 'ADD_REWARD_REDEEM_DETAIL', payload: detail }),
    removeRewardRedeemDetail: (id: any) => dispatch({ type: 'REMOVE_REWARD_REDEEM_DETAIL', payload: id }),
    clearRewardRedeemDetails: () => dispatch({ type: 'CLEAR_REWARD_REDEEM_DETAILS' }),
    setExchItem: (items: any) => dispatch({ type: 'SET_EXT_ITEM', payload: items }),
    setExchCalData: (data: any) => dispatch({ type: 'SET_EXT_CAL_DATA', payload: data }),
  }), []);

  // Optimized hash function for items to avoid JSON.stringify
  const calculateItemsHash = useCallback((items: SalesEntryItem[]) => {
    return items.reduce((hash, item, index) => {
      // Create a simple hash based on key properties
      return hash + (item.id?.toString() || '') + (item.mrp || 0) + (item.goldMrp || 0) + (item.diaMrp || 0) + index;
    }, '');
  }, []);

  // Calculate consumer offer total (exclude test offers)
  const consumerOfferTotal = useMemo(() => {
    if (!state?.items) return 0;
    return state.items.reduce((sum, item) => sum + (item.totalConsumerDiscount || 0), 0);
  }, [state?.items]);

  const totalRewardDiscount = useMemo(() => {
    if (!state?.items) return 0;
    return state.items.reduce((sum, item) => sum + (item.totalRewardDiscount || 0), 0);
  }, [state?.items]);



  // Memoize calculation inputs to prevent unnecessary recalculations
  const calculationInputs = useMemo(() => ({
    itemsLength: state?.items.length,
    itemsHash: calculateItemsHash(state?.items),
    customerId: state?.customer?.id,
    couponDiscount: state?.coupon?.discount || 0,
    discount: state?.discount,
    oldGoldAmount: state?.oldGold?.amount || 0,
    exchangeValue: state?.exchange?.totalAmount || 0,
    redemptionPoints: state?.redemption?.points || 0,
    paymentLength: state?.payment?.length || 0,
    consumerOfferTotal: consumerOfferTotal,
  }), [
    // state?.items.length,
    calculateItemsHash,
    state?.items,
    state?.customer?.id,
    state?.coupon?.discount,
    state?.discount,
    state?.oldGold?.amount,
    state?.exchange?.totalAmount,
    state?.redemption?.points,
    state?.payment?.length,
    consumerOfferTotal,
  ]);

  // Optimized calculation effect - only runs when calculation inputs change
  const performCalculation = useCallback(() => {
    if (calculationInputs.itemsLength === 0) {
      // Use React's automatic batching for state updates
      dispatch({
        type: 'UPDATE_SUMMARY', payload: {
          ornamentAmt: 0, makingCharges: 0, subAmt: 0, consumerOffer: 0,
          storeOffer: 0, discountVoucher: 0, totalOffer: 0, taxableAmount: 0,
          cgst: 0, sgst: 0, totalTax: 0, totalAmt: 0, totalDueAmt: 0,
        }
      });
      dispatch({ type: 'UPDATE_VOUCHER_DETAILS', payload: { oldGoldAmt: 0, exchangeAmt: 0, voucherAmt: 0 } });
      dispatch({ type: 'UPDATE_CUSTOMER_DETAILS', payload: { creditAmt: 0, rewardPoints: 0 } });
      return;
    }

    try {
      let calculationInput: CalculationInput = {
        items: state?.items,
        customer: state?.customer ? {
          id: state?.customer.id,
          creditAmount: state?.customerDetails.creditAmt,
          rewardPoints: state?.customerDetails.rewardPoints,
          membershipTier: 'BRONZE'
        } : undefined,
        discounts: {
          consumerOfferTotal: calculationInputs?.consumerOfferTotal ?? 0,
          coupon: state?.coupon,
          couponDiscount: calculationInputs.couponDiscount,
          storeDiscount: calculationInputs.discount,
          membershipDiscount: 0,
        },
        oldGold: state?.oldGold ? {
          weight: state?.oldGold.weight,
          purity: 22,
          currentRate: state?.oldGold.weight > 0 ? state?.oldGold.amount / state?.oldGold.weight : 0,
          amount: state?.oldGold.amount, // Pass the calculated amount directly
        } : undefined,
        exchange: state?.exchange ? {
          itemValue: state?.exchangeSalesReturnData?.returnSalesOrderModel?.totalReturnSaleOrderAmount != null
            ? state?.exchangeSalesReturnData?.returnSalesOrderModel?.totalReturnSaleOrderAmount?.toFixed(2)
            : 0
        } : undefined,
        redemption: state?.redemption ? { pointsToRedeem: state?.redemption.points } : undefined,
        payments: state?.payment && state?.payment.length > 0 ? state?.payment : undefined,
      };

      let result = calculationService.calculateSalesEntry(calculationInput);

      const invoiceRangeOffers = state?.consumerOfferDetail?.filter(offer => offer.isSelected && offer.discountTypeNmae == "Invoice Range");
      if (invoiceRangeOffers && invoiceRangeOffers.length && invoiceRangeOffers.length > 0) {
        let totalAmount = result.summary.totalAmt;
        let invoiceRangeAmount = invoiceRangeOffers.map(item => item.discountValue).reduceRight((prev, current) => prev + current, 0);
        totalAmount = totalAmount + invoiceRangeAmount;

        // invoiceRangeOffers.forEach(offer => {
        //   if (!(offer.invoiceRangeFrom <= totalAmount && offer.invoiceRangeTo > totalAmount)) {
        //     notistack.warning('Consumer offer not applicable for this invoice amount')
        //     dispatch({ type: 'TOGGLE_CONSUMER_OFFER_SELECTION', payload: offer.id });
        //     return;
        //   }
        // });
      }

      // Apply consumer offer discount to the calculation result
      const updatedSummary = {
        ...result.summary,
        consumerOffer: consumerOfferTotal,
        totalOffer: (result.summary.totalOffer || 0) + consumerOfferTotal,
        totalAmt: (result.summary.totalAmt || 0) - totalRewardDiscount,
        totalDueAmt: Math.round((result.summary.totalDueAmt || 0) - totalRewardDiscount),
      };

      dispatch({ type: 'UPDATE_SUMMARY', payload: updatedSummary });
      dispatch({ type: 'UPDATE_VOUCHER_DETAILS', payload: result.voucherDetails });
      dispatch({
        type: 'UPDATE_CUSTOMER_DETAILS', payload: {
          creditAmt: result.customerDetails.creditAmt,
          rewardPoints: result.customerDetails.rewardPointsBalance,
        }
      });
    } catch (error) {
      console.error('Calculation error:', error);
    }
  }, [calculationInputs, state?.items, state?.customer, state?.oldGold, state?.exchange, state?.redemption, state?.payment, state?.exchangeSalesReturnData?.returnSalesOrderModel]);

  // Update consumer offer in summary whenever it changes
  useEffect(() => {
    dispatch({
      type: 'UPDATE_SUMMARY',
      payload: {
        consumerOffer: consumerOfferTotal
      }
    });
  }, [consumerOfferTotal]);


  // Debounced calculation to prevent excessive recalculations
  useEffect(() => {
    performCalculation();
    // const timeoutId = setTimeout(() => {
    // }, 100); // 100ms debounce

    // return () => clearTimeout(timeoutId);
  }, [calculationInputs, state?.exchangeSalesReturnData?.returnSalesOrderModel]); // Use calculationInputs instead of performCalculation

  return (
    <SalesEntryContext.Provider value={{ coid, cid, state, dispatch, actions }}>
      {children}
    </SalesEntryContext.Provider>
  );
};

export const useSalesEntry = () => {
  const context = useContext(SalesEntryContext);
  // console.log(context?.state)
  if (context === undefined) {
    throw new Error('useSalesEntry must be used within a SalesEntryProvider');
  }
  return context;
};